import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  Users,
  Trash2,
  Search,
  UserCheck,
  UserX,
  Crown,
  Mail,
  Calendar,
  FileText,
  TrendingUp,
  Shield,
  Gift,
  CreditCard,
  Loader2,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = ({ token }) => {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const getHeaders = () => ({
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  });

  const fetchData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const headers = getHeaders();
      const [usersRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/users`, { headers }),
        fetch(`${API}/admin/stats`, { headers })
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      } else {
        console.error("Users API error:", usersRes.status);
        toast.error(t('admin.users.loadError'));
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      } else {
        console.error("Stats API error:", statsRes.status);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error(t('admin.users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always try to fetch when component mounts or token changes
    fetchData();
  }, [token]);

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      const headers = getHeaders();
      const res = await fetch(`${API}/admin/users/${userToDelete.user_id}`, {
        method: "DELETE",
        headers,
      });

      if (res.ok) {
        setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
        toast.success(t('admin.users.deleteSuccess'));
        fetchData(); // Refresh stats
      } else {
        const error = await res.json();
        toast.error(error.detail || t('admin.users.deleteError'));
      }
    } catch (error) {
      toast.error(t('admin.users.deleteError'));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    } catch {
      return "-";
    }
  };

  const filteredUsers = users.filter(u => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = (
      u.email?.toLowerCase().includes(search) ||
      u.name?.toLowerCase().includes(search) ||
      u.user_id?.toLowerCase().includes(search)
    );
    
    // Apply filter type
    let matchesFilter = true;
    if (filterType === "invited") {
      matchesFilter = u.is_invited_user || u.invite_analyses_remaining > 0;
    } else if (filterType === "paying") {
      matchesFilter = u.subscription_type === "subscription" || u.subscription_type === "single";
    } else if (filterType === "free") {
      matchesFilter = !u.is_admin && !u.is_invited_user && !u.invite_analyses_remaining && !u.subscription_type;
    } else if (filterType === "admin") {
      matchesFilter = u.is_admin;
    }
    
    return matchesSearch && matchesFilter;
  });

  const getUserStatus = (user) => {
    if (user.is_admin) return { label: t('admin.users.statusAdmin'), color: "text-volt", icon: Crown };
    if (user.is_invited_user) return { label: t('admin.users.statusInvitedUnlimited'), color: "text-cyan", icon: Gift };
    if (user.invite_analyses_remaining > 0) return { label: t('admin.users.statusInvitedCount', { count: user.invite_analyses_remaining }), color: "text-cyan", icon: Gift };
    if (user.subscription_type === "subscription") return { label: t('admin.users.statusSubscription'), color: "text-green-400", icon: CreditCard };
    if (user.subscription_type === "single") return { label: t('admin.users.statusSingle'), color: "text-yellow-400", icon: FileText };
    return { label: t('admin.users.statusFree'), color: "text-zinc-400", icon: UserX };
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-volt" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {t('admin.users.pleaseLogin')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {/* Refresh Button at top */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {users.length > 0 ? t('admin.users.usersLoaded', { count: users.length }) : t('admin.users.loadingUsers')}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="border-white/10"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('admin.users.refresh')}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-volt" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.users.total}</p>
                <p className="text-xs text-muted-foreground">{t('admin.users.stats.registeredUsers')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-cyan" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.users.recent_30_days}</p>
                <p className="text-xs text-muted-foreground">{t('admin.users.stats.new30Days')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.analyses.total}</p>
                <p className="text-xs text-muted-foreground">{t('admin.users.stats.totalAnalyses')}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Gift className="h-8 w-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.invites.active}</p>
                <p className="text-xs text-muted-foreground">{t('admin.users.stats.activeInvites')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Stats Row */}
      {stats && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
            <div>
              <p className="text-lg font-semibold text-volt">{stats.users.admins}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.admins')}</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-cyan">{stats.users.invited}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.invited')}</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-green-400">{stats.users.paying}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.paying')}</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-yellow-400">{stats.analyses.recent_30_days}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.analyses30Days')}</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-purple-400">{stats.invites.total}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.invitations')}</p>
            </div>
            <div>
              <p className="text-lg font-semibold text-pink-400">{stats.invites.used}</p>
              <p className="text-xs text-muted-foreground">{t('admin.users.stats.used')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('admin.users.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-10 rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-volt"
        >
          <option value="all">{t('admin.users.filterAll')}</option>
          <option value="invited">{t('admin.users.filterInvited')}</option>
          <option value="paying">{t('admin.users.filterPaying')}</option>
          <option value="free">{t('admin.users.filterFree')}</option>
          <option value="admin">{t('admin.users.filterAdmin')}</option>
        </select>
      </div>

      {/* Users List */}
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          {t('admin.users.showingUsers', { shown: filteredUsers.length, total: users.length })}
        </p>
        
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
          {filteredUsers.map((u) => {
            const status = getUserStatus(u);
            const StatusIcon = status.icon;
            
            return (
              <div
                key={u.user_id}
                className="bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg bg-white/5 ${status.color}`}>
                      <StatusIcon className="h-5 w-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-white truncate">
                          {u.name || t('admin.users.noName')}
                        </p>
                        {u.is_admin && (
                          <span className="text-xs bg-volt/20 text-volt px-2 py-0.5 rounded">
                            {t('admin.users.statusAdmin')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3" />
                          {u.email}
                        </span>
                      </div>
                    </div>
                    
                    <div className="hidden md:flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-semibold text-white">{u.analysis_count || 0}</p>
                        <p className="text-xs text-muted-foreground">{t('admin.users.analyses')}</p>
                      </div>
                      <div className="text-center">
                        <p className={`font-semibold ${status.color}`}>{status.label}</p>
                        <p className="text-xs text-muted-foreground">{t('admin.users.status')}</p>
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-white">{formatDate(u.created_at)}</p>
                        <p className="text-xs text-muted-foreground">{t('admin.users.registered')}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {!u.is_admin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(u);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Mobile: Additional info */}
                <div className="md:hidden mt-3 pt-3 border-t border-white/10 grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <p className="font-semibold text-white">{u.analysis_count || 0}</p>
                    <p className="text-muted-foreground">{t('admin.users.analyses')}</p>
                  </div>
                  <div>
                    <p className={`font-semibold ${status.color}`}>{status.label}</p>
                    <p className="text-muted-foreground">{t('admin.users.status')}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-white">{formatDate(u.created_at)}</p>
                    <p className="text-muted-foreground">{t('admin.users.registered')}</p>
                  </div>
                </div>
              </div>
            );
          })}
          
          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? t('admin.users.noUsersFound') : t('admin.users.noUsersYet')}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-red-400">{t('admin.users.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.users.deleteDialog.confirm', { email: userToDelete?.email })}
              <br /><br />
              <span className="text-red-400">
                {t('admin.users.deleteDialog.warning')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
